import { z } from "zod";

export const PLATFORMS = ["tiktok", "instagram", "youtube"] as const;
export type Platform = (typeof PLATFORMS)[number];

export const SNAPSHOT_TYPES = ["daily", "24h", "7d"] as const;
export type SnapshotType = (typeof SNAPSHOT_TYPES)[number];

// ─── Project ────────────────────────────────────────────────
// The monitoring intent: a named bucket of keywords + which platform pages to scrape.
// `account_name` is preserved as the column name (rename deferred to keep this migration
// rename-only); the UI labels it "Project name".
export const ProjectRowSchema = z.object({
  id: z.number().int(),
  account_name: z.string(),
  notes: z.string().nullable(),
  monitor_keywords: z.string(), // JSON-encoded array of strings
  enabled: z.number().int(),    // 0 | 1
  created_at: z.number().int(),
  updated_at: z.number().int(),
});
export type ProjectRow = z.infer<typeof ProjectRowSchema>;

export type Project = Omit<ProjectRow, "monitor_keywords" | "enabled"> & {
  monitor_keywords: string[];
  enabled: boolean;
};

export function parseProject(row: ProjectRow): Project {
  let kws: unknown;
  try {
    kws = JSON.parse(row.monitor_keywords);
  } catch {
    kws = [];
  }
  return {
    ...row,
    monitor_keywords: Array.isArray(kws) ? kws.filter((x): x is string => typeof x === "string") : [],
    enabled: row.enabled === 1,
  };
}

// ─── ProjectPage ────────────────────────────────────────────
// One row per (project, platform, profile_url). A project can have multiple pages on the
// same platform (e.g. main + brand-collab TikToks); each page is what the scraper actually
// fans out over. Each page also references a KOC (the publisher who owns the account).
export const ProjectPageRowSchema = z.object({
  id: z.number().int(),
  project_id: z.number().int(),
  koc_id: z.number().int().nullable(),
  platform: z.enum(PLATFORMS),
  profile_url: z.string(),
  label: z.string().nullable(),
  enabled: z.number().int(),
  created_at: z.number().int(),
  updated_at: z.number().int(),
});
export type ProjectPageRow = z.infer<typeof ProjectPageRowSchema>;

export type ProjectPage = Omit<ProjectPageRow, "enabled"> & {
  enabled: boolean;
};

export function parseProjectPage(row: ProjectPageRow): ProjectPage {
  return { ...row, enabled: row.enabled === 1 };
}

// ─── KOC ────────────────────────────────────────────────────
// A content creator/influencer. Independent entity, no keywords. Can appear in multiple
// projects (via project_pages) and in manually-imported videos.
export const KocRowSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  notes: z.string().nullable(),
  created_at: z.number().int(),
  updated_at: z.number().int(),
});
export type KocRow = z.infer<typeof KocRowSchema>;

export type Koc = KocRow;

export function parseKoc(row: KocRow): Koc {
  return row;
}

// ─── Video ──────────────────────────────────────────────────
export const VideoRowSchema = z.object({
  id: z.number().int(),
  page_id: z.number().int().nullable(),
  project_id: z.number().int(),
  koc_id: z.number().int().nullable(),
  platform: z.enum(PLATFORMS),
  platform_video_id: z.string(),
  url: z.string(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  publish_time: z.number().int(),
  thumbnail_url: z.string().nullable(),
  duration_seconds: z.number().int().nullable(),
  keyword_hits: z.string(),
  has_keyword_hit: z.number().int(),
  first_seen_at: z.number().int(),
  last_scraped_at: z.number().int(),
});
export type VideoRow = z.infer<typeof VideoRowSchema>;

// ─── VideoSnapshot ──────────────────────────────────────────
export const VideoSnapshotRowSchema = z.object({
  id: z.number().int(),
  video_id: z.number().int(),
  snapshot_type: z.enum(SNAPSHOT_TYPES),
  captured_at: z.number().int(),
  hours_since_publish: z.number(),
  view_count: z.number().int().nullable(),
  like_count: z.number().int().nullable(),
  comment_count: z.number().int().nullable(),
  share_count: z.number().int().nullable(),
  save_count: z.number().int().nullable(),
  raw_json: z.string().nullable(),
});
export type VideoSnapshotRow = z.infer<typeof VideoSnapshotRowSchema>;

// ─── Normalized scraping output (platform-agnostic) ─────────
// What every platform normalizer returns per video.
export type NormalizedVideo = {
  platform: Platform;
  platform_video_id: string;
  url: string;
  title: string | null;
  description: string | null;
  publish_time: number; // unix seconds
  thumbnail_url: string | null;
  duration_seconds: number | null;
  view_count: number | null;
  like_count: number | null;
  comment_count: number | null;
  share_count: number | null;
  save_count: number | null;
  raw: unknown;
};
